import { DriverAppFulfilmentFailureReason } from 'generated/graphql'

export const COLOR_DRIVER_APP = '#30A46C'
export const COLOR_CX = '#E5484D'
export const COLOR_TOTAL_JOBS = '#7B61FF'
export const COLOR_FULFILMENT_RATE = '#3B82F6'
export const COLOR_NOT_FULFILLED = '#9E9E9E'

export const COLOR_CX_CHEAPER = '#0E9F9F'
export const COLOR_DRIVER_APP_CHEAPER = '#7B61FF'
export const COLOR_PRICE_LEVEL = '#9E9E9E'

export const FAILURE_REASON_COLORS: Record<DriverAppFulfilmentFailureReason, string> = {
  [DriverAppFulfilmentFailureReason.NoDriverAppBids]: '#7B61FF',
  [DriverAppFulfilmentFailureReason.DriverAppBidMoreExpensive]: '#F5A524',
  [DriverAppFulfilmentFailureReason.CxSelectedDespiteCheaperBid]: '#E5484D',
  [DriverAppFulfilmentFailureReason.PricesNotComparable]: '#9E9E9E',
}

export const FAILURE_REASON_INFO: Record<DriverAppFulfilmentFailureReason, string> = {
  [DriverAppFulfilmentFailureReason.NoDriverAppBids]:
    'The job was fulfilled through CX without receiving any live Driver App bids. Withdrawn and ' +
    'dismissed bids are excluded.',
  [DriverAppFulfilmentFailureReason.DriverAppBidMoreExpensive]:
    'The job received at least one live Driver App bid, but the lowest bid was higher than the ' +
    'accepted CX price. Both prices are converted to GBP for comparison.',
  [DriverAppFulfilmentFailureReason.CxSelectedDespiteCheaperBid]:
    'The job received a live Driver App bid that was equal to or lower than the accepted CX ' +
    'price, but was still fulfilled through CX. An equal price is included in this category.',
  [DriverAppFulfilmentFailureReason.PricesNotComparable]:
    'The job received a live Driver App bid, but the Driver App and CX prices cannot be ' +
    'compared. This may be because no accepted CX price is available, one of the prices is ' +
    'zero, or an exchange rate is not available for one of the currencies.',
}

export const DRIVER_APP_FULFILMENT_CHART_HEIGHT = 320
export const DRIVER_APP_FULFILMENT_DONUT_HEIGHT = 260

export const FULFILMENT_TREND_LABEL_PADDING_TOP = 18
export const FULFILMENT_TREND_LABEL_OFFSET = 8
export const FULFILMENT_TREND_DAILY_POINT_ALPHA = 0.35
export const FULFILMENT_TREND_DAILY_POINT_RADIUS = 2.5
export const FULFILMENT_TREND_MAX_POINT_LABELS = 12

export const DRIVER_APP_FULFILMENT_EARLIEST_DATE = new Date(2024, 4, 1) // 1 May 2024

export const DRIVER_APP_FULFILMENT_RELIABLE_FROM_DATE = new Date(2026, 7, 1) // 1 August 2026

export type PriceDifferenceView = 'percentage' | 'count' | 'value'

export const PRICE_DIFFERENCE_VIEWS: { value: PriceDifferenceView; label: string }[] = [
  { value: 'percentage', label: '% of failed jobs' },
  { value: 'count', label: 'Number of jobs' },
  { value: 'value', label: 'Value (£)' },
]

// Fulfilment ring chart sizing and the faded remainder track.
export const FULFILMENT_RING_SIZE = 88
export const FULFILMENT_RING_TRACK_ALPHA = 0.2

export const DRIVER_APP_FULFILMENT_INFO =
  'Every job posted to the Driver App during the selected period is classified by the network ' +
  'that ultimately fulfilled it. A job is considered fulfilled once the booking reaches Complete ' +
  'or Invoice status. Jobs still in progress are included in the total number posted but are ' +
  'excluded from the fulfilment rate. Where a job was fulfilled through CX, the page also shows ' +
  'why the Driver App was not selected and compares the Driver App and CX prices.'

export const DRIVER_APP_FULFILMENT_FOOTNOTES = [
  'Each job is assigned to the date it was posted to the Driver App and is ' +
    'considered fulfilled once the booking reaches Complete or Invoice status, ' +
    'through whichever network completed its final delivery.',
  'Jobs still in progress are included in the total number posted but are ' +
    'excluded from the fulfilment rate. Cancelled and quote-lost jobs are excluded.',
  'Only live Driver App bids are taken into account, and prices in other ' +
    'currencies are converted to GBP for comparison.',
  'Figures are refreshed twice daily and may lag recent activity by up to ' + 'twelve hours.',
]

export const FULFILMENT_TOTAL_INFO =
  'The total number of jobs posted to the Driver App during the selected period. Each job is ' +
  'included based on the date it was posted, regardless of its subsequent outcome. Cancelled ' +
  'and quote-lost jobs are excluded.'

export const FULFILMENT_DRIVER_APP_INFO =
  'The total number of jobs fulfilled through the Driver App network. A job is counted as ' +
  'fulfilled once the booking reaches Complete or Invoice status.'

export const FULFILMENT_CX_INFO =
  'The total number of jobs fulfilled through the Courier Exchange (CX) marketplace. A job is ' +
  'counted as fulfilled once the booking reaches Complete or Invoice status.'

export const FULFILMENT_RATE_INFO =
  'The percentage of fulfilled jobs completed through the Driver App rather than CX. Jobs that ' +
  'are not yet complete are excluded from the calculation.'

export const FAILURE_REASONS_CARD_INFO =
  'A breakdown of jobs fulfilled through CX, grouped by the reason the Driver App was not ' +
  'selected. Where prices are available, the lowest Driver App bid is compared with the accepted ' +
  'CX price. Jobs with missing, zero, or non-comparable pricing are shown separately. Together, ' +
  'these categories account for all jobs fulfilled through CX.'

export const FULFILMENT_TREND_INFO =
  'Shows the percentage of jobs with a known outcome that were fulfilled through the Driver App, ' +
  'calculated over a rolling seven-day period ending on each date. The rate is calculated from ' +
  'the combined seven-day totals, rather than as an average of the daily percentages, so days ' +
  'with fewer jobs do not carry the same weight as busier days. Jobs are assigned to the date ' +
  'they were posted. Data from the six days before the selected period is also included so that ' +
  'the first point on the chart represents a complete seven-day window.'

export const PRICE_DIFFERENCE_INFO =
  'Shows how the accepted CX price compared with the lowest Driver App bid for jobs fulfilled ' +
  'through CX. The percentage difference is calculated as (CX price − lowest Driver App bid) ÷ ' +
  'lowest Driver App bid × 100. Negative values indicate that CX was cheaper, while positive ' +
  'values indicate that the Driver App was cheaper. Jobs are grouped into percentage difference ' +
  'ranges, with all prices converted to GBP for comparison.'

export const PRICE_DIFFERENCE_VIEW_INFO = [
  '“Failed jobs” are jobs that were posted to the Driver App but were ultimately fulfilled ' +
    'through CX — in other words, jobs that the Driver App did not win. Only jobs that can be ' +
    'priced are included in this chart. To be assigned to a band, a job must have both a live ' +
    'Driver App bid and an accepted CX price, with both values converted to GBP.',
  '% of failed jobs — shows each band as a percentage of all failed jobs included in the chart. ' +
    'This makes it easier to understand and compare the overall distribution regardless of the ' +
    'total number of jobs.',
  'Number of jobs — shows the actual number of failed jobs in each band. This provides ' +
    'additional context where a band contains only a small number of jobs and a percentage ' +
    'alone could make the result appear more significant than it is.',
  'Value (£) — groups the same jobs by the price difference in pounds, calculated as (CX price ' +
    '− lowest Driver App bid), rather than by percentage difference. This allows each band to be ' +
    'read as a monetary amount rather than a ratio.',
].join('\n\n')
